import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import './CollaborativeEditor.css';

// Editor colaborativo compartido (Tiptap + Yjs + Hocuspocus, canal `/yjs`). Lo usan las tareas
// (`TaskDescriptionEditor`, `PublicTaskView`) y, desde la integración main→dev epic D2, los
// documentos (`features/docs/components/DocsCollaborativeEditor`). Las props nuevas de D2
// (`editable`, `onActiveUsersChange`, `onEditorReady`, `guestToken`, `editorAttributes`) son
// aditivas — sin pasarlas, el comportamiento previo de tareas queda igual.
export function CollaborativeEditor({
    documentId,
    initialHtml = '',
    onUpdate,
    onUpload, // (file, editor) => Promise<void>
    onActiveUsersChange, // (uniqueUsers[], { windowsCount }) => void
    onEditorReady, // (editor) => void
    onSelectionUpdate, // (editor) => void
    placeholder = 'Escribe aquí...',
    currentUser,
    className = '',
    customExtensions = [],
    editable = true,
    editorAttributes = {},
}) {
    // Unique color for the user's cursor — estable por identidad (no `Math.random()`, que
    // recreaba el editor en cada render).
    const currentUserId = currentUser?.id || currentUser?.uid || currentUser?.dbId || '';
    const userColor = useMemo(() => {
        const colors = ['#f783ac', '#8ce99a', '#74c0fc', '#ffa94d', '#d0bfff', '#ff8787', '#94d82d', '#4dabf7'];
        if (!currentUserId) return colors[0];
        let hash = 0;
        for (let i = 0; i < String(currentUserId).length; i++) {
            hash = String(currentUserId).charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }, [currentUserId]);

    // Provide the latest callbacks to tiptap without recreating the editor
    const latestRef = useRef({ onUpdate, onUpload, onActiveUsersChange, onEditorReady, onSelectionUpdate });
    const editorRef = useRef(null);
    const hasHydratedRef = useRef(false);
    useEffect(() => {
        latestRef.current.onUpdate = onUpdate;
        latestRef.current.onUpload = onUpload;
        latestRef.current.onActiveUsersChange = onActiveUsersChange;
        latestRef.current.onEditorReady = onEditorReady;
        latestRef.current.onSelectionUpdate = onSelectionUpdate;
    }, [onUpdate, onUpload, onActiveUsersChange, onEditorReady, onSelectionUpdate]);

    const [ydoc] = useState(() => new Y.Doc());
    const [provider] = useState(() => {
        const host = window.location.hostname === 'localhost' ? 'ws://localhost:3000' : `wss://${window.location.host}`;
        return new HocuspocusProvider({
            url: `${host}/yjs`,
            name: documentId,
            document: ydoc,
        });
    });

    useEffect(() => {
        return () => {
            provider.destroy();
            ydoc.destroy();
        };
    }, [provider, ydoc]);

    // Awareness → lista de usuarios activos deduplicada por id (integración main→dev, epic D2:
    // reemplaza `useDocumentPresence`, que no deduplicaba y montaba un provider aparte).
    useEffect(() => {
        if (!provider?.awareness || !onActiveUsersChange) return;
        const handle = () => {
            const states = Array.from(provider.awareness.getStates().values());
            const editing = states.filter((s) => s.user && s.user.name && s.user.canEdit !== false);
            const byId = new Map();
            for (const s of editing) {
                const u = s.user;
                const key = String(u.id || u.name);
                if (!byId.has(key)) byId.set(key, { ...u, windowsCount: 1 });
                else byId.get(key).windowsCount += 1;
            }
            latestRef.current.onActiveUsersChange?.(Array.from(byId.values()), { windowsCount: editing.length });
        };
        provider.awareness.on('change', handle);
        handle();
        return () => provider.awareness.off('change', handle);
    }, [provider, onActiveUsersChange]);

    // Mantener la awareness al día con el perfil real del usuario.
    useEffect(() => {
        if (!provider?.awareness || !currentUser) return;
        const avatar = currentUser['link-profile-img-thumb'] || currentUser['link-profile-img']
            || currentUser.image || currentUser.avatar_url || currentUser.avatar || currentUser.photo;
        provider.awareness.setLocalStateField('user', {
            id: currentUser.id || currentUser.dbId || currentUserId,
            name: currentUser.nick || currentUser.name || 'Anónimo',
            color: userColor,
            avatar,
            canEdit: Boolean(editable),
        });
    }, [provider, currentUser, userColor, currentUserId, editable]);

    const extensionsRef = useRef(customExtensions);
    const currentUserRef = useRef(currentUser);

    useEffect(() => {
        extensionsRef.current = customExtensions;
    }, [customExtensions]);

    const extensions = useMemo(() => {
        return [
            // StarterKit v3 ya incluye Link, Underline y UndoRedo. `undoRedo: false` porque
            // Collaboration trae su propio historial (no `history` — renombrado en v3).
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                undoRedo: false,
                link: { openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } },
            }),
            TaskList,
            TaskItem.configure({ nested: false }),
            TextStyle,
            Color,
            Placeholder.configure({ placeholder: () => placeholder }),
            Collaboration.configure({ document: ydoc }),
            CollaborationCursor.configure({
                provider,
                user: {
                    name: currentUserRef.current?.nick || currentUserRef.current?.name || 'Anónimo',
                    color: userColor,
                    avatar: currentUserRef.current?.avatar_url || currentUserRef.current?.['link-profile-img'],
                },
            }),
            ...extensionsRef.current,
        ];
    }, [provider, ydoc, userColor]);

    const editor = useEditor({
        extensions,
        content: null,
        autofocus: false,
        immediatelyRender: false,
        editable,
        editorProps: {
            attributes: { class: `collaborative-editor-content ${className}`, ...editorAttributes },
            handleDrop: (_view, event) => {
                const file = event.dataTransfer?.files?.[0];
                if (!file) return false;
                event.preventDefault();
                latestRef.current.onUpload?.(file, editorRef.current);
                return true;
            },
            handlePaste: (_view, event) => {
                const file = event.clipboardData?.files?.[0];
                if (!file) return false;
                event.preventDefault();
                latestRef.current.onUpload?.(file, editorRef.current);
                return true;
            },
            transformPastedHTML: (html) => {
                if (html.includes('docs-internal-guid')) {
                    return html.replace(/<b[^>]*id="docs-internal-guid-[^>]*>/gi, '').replace(/<\/b>\s*$/i, '');
                }
                return html;
            },
        },
        onUpdate({ editor, transaction }) {
            latestRef.current.onUpdate?.(editor.getHTML());
        },
        onSelectionUpdate({ editor }) {
            latestRef.current.onSelectionUpdate?.(editor);
        },
    }, [extensions]); // Re-create editor only when extensions (provider) change

    useEffect(() => {
        editorRef.current = editor;
        if (editor && latestRef.current.onEditorReady) latestRef.current.onEditorReady(editor);

        if (!editor || !provider) return;

        const handleSynced = () => {
            // Populate the Y.Doc from DB if Hocuspocus sent an empty document.
            if (!hasHydratedRef.current && editor.isEmpty && initialHtml) {
                editor.commands.setContent(initialHtml);
            }
            hasHydratedRef.current = true;
        };

        if (provider.isSynced) handleSynced();
        provider.on('synced', handleSynced);

        // Fallback de hidratación (integración main→dev, epic D2): si el servidor `/yjs` no
        // responde (entornos sin servidor de colaboración real — mismo caso que los e2e
        // ignoran `ws://.../yjs`), `synced` nunca llega y el editor quedaría vacío. Tras un
        // margen corto, si sigue vacío y hay contenido inicial, se hidrata igual.
        const fallback = setTimeout(handleSynced, 1500);
        return () => { provider.off('synced', handleSynced); clearTimeout(fallback); };
    }, [editor, provider, initialHtml]);

    useEffect(() => {
        if (editor && editor.isEditable !== editable) editor.setEditable(editable);
    }, [editor, editable]);

    if (!editor) {
        return <div className="collaborative-editor-loading" style={{ padding: '24px 32px', color: '#a3a3a3' }}>Conectando...</div>;
    }

    return (
        <div className="collaborative-editor-wrapper">
            <EditorContent editor={editor} />
        </div>
    );
}
